import React, { useMemo, useState } from "react";
import { Button, Form, InputGroup, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../_helper/CurrencyContext";
import { useDisableScrollOnFocus } from "../../../Hooks/useDisableScrollOnFocus";
import { toMoneyNumber } from "../../../utils/financeEngine";
import {
	BillingInvestigationsAdjustments,
	BillingServicesAdjustments,
} from "../../PharmacySales/NewSales/components/BillingServicesSection";
import {
	buildGeneralServiceOptions,
	buildInvestigationOptions,
	convertLineAmount,
} from "../../PharmacySales/NewSales/utils/billingUtils";
import { formatAmount } from "../utils/patientAccountUtils";

const SECTION_ACCENTS = {
	services: "services",
	investigations: "lab",
	medicines: "pharmacy",
};

export const SourceBadge = ({ variant, children }) => (
	<span className={`cp-badge cp-badge--${variant}`}>{children}</span>
);

export const SectionBlock = ({
	accent = "services",
	icon,
	title,
	total,
	showTotal = true,
	currency,
	getCurrencySymbol,
	isOpen,
	onToggle,
	children,
	footer,
	itemCount = 0,
}) => (
	<div
		className={`cp-section cp-section--${SECTION_ACCENTS[accent] || accent} ${
			isOpen ? "cp-section--open" : ""
		}`}
	>
		<button type="button" className="cp-section-head" onClick={onToggle} aria-expanded={isOpen}>
			<span className="cp-section-head-left">
				<span className="cp-section-icon" aria-hidden="true">
					<i className={`fa ${icon}`} />
				</span>
				<span className="cp-section-title">
					{title}
					{itemCount > 0 && (
						<span className="cp-section-count-inline">({itemCount})</span>
					)}
				</span>
			</span>
			<span className="cp-section-head-right">
				{showTotal && total != null && (
					<strong className="cp-section-total">
						{formatAmount(total, currency, getCurrencySymbol)}
					</strong>
				)}
				<i className={`fa fa-chevron-${isOpen ? "up" : "down"}`} aria-hidden="true" />
			</span>
		</button>
		{isOpen && (
			<div className="cp-section-body">
				{children}
				{footer}
			</div>
		)}
	</div>
);

export const SectionDivider = ({ title }) => (
	<div className="cp-section-divider-label">
		<span>{title}</span>
	</div>
);

export const LineAmountInput = ({
	item,
	updateFn,
	disabled,
	displayCurrency,
	convertCurrency,
	pharmacyCurrency,
	allowedCurrencies,
	disableScrollProps,
}) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState("");

	const value = useMemo(() => {
		if (!item?.amount && item?.amount !== 0) return "";
		return String(
			convertLineAmount(
				item.amount,
				item.currency,
				displayCurrency,
				convertCurrency,
				pharmacyCurrency,
				allowedCurrencies
			)
		);
	}, [allowedCurrencies, convertCurrency, displayCurrency, item, pharmacyCurrency]);

	const commit = (raw) => {
		const displayAmount = toMoneyNumber(raw || 0);
		const nativeAmount = convertLineAmount(
			displayAmount,
			displayCurrency,
			item.currency,
			convertCurrency,
			pharmacyCurrency,
			allowedCurrencies
		);
		updateFn(item.id, nativeAmount);
		setEditing(false);
		setDraft("");
	};

	return (
		<Form.Control
			size="sm"
			type="text"
			className="cp-amount-input"
			disabled={disabled}
			value={editing ? draft : value}
			onFocus={(e) => {
				setEditing(true);
				setDraft(value);
				disableScrollProps?.onFocus?.(e);
			}}
			onChange={(e) => {
				const raw = e.target.value;
				if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) setDraft(raw);
			}}
			onBlur={(e) => {
				commit(e.target.value);
				disableScrollProps?.onBlur?.(e);
			}}
			onKeyDown={disableScrollProps?.onKeyDown}
		/>
	);
};

export const PrescribedMedicinesBlock = ({ prescriptions, t }) => {
	if (!prescriptions?.length) return null;

	return (
		<div className="cp-prescribed-block">
			<div className="cp-block-label">{t("Prescribed Medicines")}</div>
			<p className="cp-block-hint">{t("Doctor prescription — reference for dispensing.")}</p>
			<div className="table-responsive">
				<Table size="sm" className="cp-table cp-table--prescribed mb-0">
					<thead>
						<tr>
							<th>{t("Medicine")}</th>
							<th>{t("Dosage")}</th>
							<th className="cp-col-qty">{t("Qty")}</th>
							<th>{t("Duration")}</th>
							<th>{t("Instruction")}</th>
						</tr>
					</thead>
					<tbody>
						{prescriptions.map((item, index) => (
							<tr key={item._id || index}>
								<td>
									<div className="cp-item-title">{item.medication_name}</div>
									{item.salt_composition && (
										<div className="cp-item-sub">{item.salt_composition}</div>
									)}
									{item.product_form && (
										<span className="cp-form-tag">{item.product_form}</span>
									)}
								</td>
								<td>{item.dosage_name || item.dosage || "-"}</td>
								<td>{item.qty ?? "-"}</td>
								<td>{item.duration || "-"}</td>
								<td className="text-muted">{item.instruction || "-"}</td>
							</tr>
						))}
					</tbody>
				</Table>
			</div>
		</div>
	);
};

const PharmacyPurchasesBlock = ({ pharmacyItems, displayCurrency, getCurrencySymbol, t }) => {
	if (!pharmacyItems?.length) return null;

	return (
		<div className="cp-purchases-block">
			<div className="cp-block-label">{t("Pharmacy Purchases")}</div>
			<div className="table-responsive">
				<Table size="sm" className="cp-table cp-table--purchases mb-0">
					<thead>
						<tr>
							<th>{t("Item")}</th>
							<th className="text-end">{t("MRP")}</th>
							<th className="cp-col-qty text-center">{t("Qty")}</th>
							<th className="text-end">{t("Taxable")}</th>
							<th className="text-end">{t("Tax")}</th>
							<th className="text-end">{t("Gross")}</th>
						</tr>
					</thead>
					<tbody>
						{pharmacyItems.map((item, index) => (
							<tr key={item.item_id || index}>
								<td>
									<div className="cp-item-title">{item.item || item.brand_name}</div>
									{item.medicine_name && (
										<div className="cp-item-sub">{item.medicine_name}</div>
									)}
									{item.product_form && (
										<span className="cp-form-tag">{item.product_form}</span>
									)}
								</td>
								<td className="text-end">
									{formatAmount(item.unit_price, displayCurrency, getCurrencySymbol)}
								</td>
								<td className="text-center">{item.qty}</td>
								<td className="text-end">
									{formatAmount(item.sub_total, displayCurrency, getCurrencySymbol)}
								</td>
								<td className="text-end">
									<span className="cp-tax-cell">
										{formatAmount(item.tax, displayCurrency, getCurrencySymbol)}
										{item.tax_percentage > 0 && (
											<small className="cp-tax-pct">{item.tax_percentage}%</small>
										)}
									</span>
								</td>
								<td className="text-end fw-semibold">
									{formatAmount(item.net_total, displayCurrency, getCurrencySymbol)}
								</td>
							</tr>
						))}
					</tbody>
				</Table>
			</div>
		</div>
	);
};

const CollectPaymentBillingPanel = ({
	invoice,
	loading,
	billingFlags,
	displayCurrency,
	defaultCurrency,
	posTaxType,
	pharmacyStateCode,
	patientStateCode,
	serviceItems,
	investigationItems,
	pharmacyItems,
	servicesBillingTotals,
	investigationsBillingTotals,
	billingSummary,
	servicesDiscountType,
	servicesDiscountValue,
	servicesTaxConfig,
	investigationsDiscountType,
	investigationsDiscountValue,
	investigationsTaxConfig,
	generalMasterList,
	labMasterList,
	sectionOpen,
	toggleSection,
	updateServiceAmount,
	updateServiceQuantity,
	updateInvestigationAmount,
	updateInvestigationQuantity,
	removeServiceItem,
	removeInvestigation,
	addServiceRow,
	addInvestigationRow,
	selectServiceForRow,
	selectInvestigationForRow,
	updateServicesDiscount,
	updateInvestigationsDiscount,
	updateServicesTaxConfig,
	updateInvestigationsTaxConfig,
}) => {
	const { t } = useTranslation();
	const { getCurrencySymbol, allowedCurrencies, convertCurrency } = useCurrency();
	const disableScrollProps = useDisableScrollOnFocus();

	const prescriptions = invoice?.prescriptions || [];
	const hasPharmacyPurchases = pharmacyItems.length > 0;
	const showMedicinesSection = prescriptions.length > 0 || hasPharmacyPurchases;

	const showLabSection = billingFlags?.showLabSection === true;
	const showServicesTax = billingFlags?.showServicesTax === true;
	const showLabTax = billingFlags?.showLabTax === true;

	const generalServiceOptions = useMemo(
		() =>
			buildGeneralServiceOptions(
				generalMasterList,
				getCurrencySymbol,
				defaultCurrency,
				allowedCurrencies
			),
		[allowedCurrencies, defaultCurrency, generalMasterList, getCurrencySymbol]
	);

	const labInvestigationOptions = useMemo(
		() =>
			buildInvestigationOptions(
				labMasterList,
				getCurrencySymbol,
				defaultCurrency,
				allowedCurrencies
			),
		[allowedCurrencies, defaultCurrency, getCurrencySymbol, labMasterList]
	);

	const usedServiceIds = new Set(
		serviceItems.filter((item) => item.service_id).map((item) => String(item.service_id))
	);
	const usedTestIds = new Set(
		investigationItems.filter((item) => item.test_id).map((item) => String(item.test_id))
	);

	const amountInputProps = {
		displayCurrency,
		convertCurrency,
		pharmacyCurrency: defaultCurrency,
		allowedCurrencies,
		disableScrollProps,
	};

	const isReadOnlyService = (item) =>
		item.isConsultation || (item.isPrescribed && !item.isNewRow);
	const isSelectableService = (item) =>
		!item.isConsultation && (item.isNewRow || item.source === "added");
	const isReadOnlyInvestigation = (item) => item.isPrescribed && !item.isNewRow;

	if (loading) {
		return <div className="cp-loading">{t("Loading billing details...")}</div>;
	}

	return (
		<div className="cp-billing-panel">
			<SectionBlock
				accent="services"
				icon="fa-stethoscope"
				title={t("Clinic Consultation & Services")}
				total={billingSummary.services_total ?? 0}
				currency={displayCurrency}
				getCurrencySymbol={getCurrencySymbol}
				isOpen={sectionOpen.services}
				onToggle={() => toggleSection("services")}
				itemCount={serviceItems.length}
				footer={
					<>
						<SectionDivider title={t("Discount & Tax")} />
						<div className="cp-adjustments-body">
							<BillingServicesAdjustments
								t={t}
								servicesBilling={servicesBillingTotals}
								servicesTaxConfig={servicesTaxConfig}
								servicesDiscountType={servicesDiscountType}
								servicesDiscountValue={servicesDiscountValue}
								onDiscountTypeChange={(type) => updateServicesDiscount({ discountType: type })}
								onDiscountValueChange={(value) =>
									updateServicesDiscount({ discountValue: value })
								}
								onTaxConfigChange={updateServicesTaxConfig}
								displayCurrency={displayCurrency}
								getCurrencySymbol={getCurrencySymbol}
								posTaxType={posTaxType}
								pharmacyStateCode={pharmacyStateCode}
								patientStateCode={patientStateCode}
								disableScrollProps={disableScrollProps}
								showTax={showServicesTax}
							/>
						</div>
					</>
				}
			>
				<div className="table-responsive cp-table-wrap">
					<Table size="sm" className="cp-table mb-0">
						<thead>
							<tr>
								<th>{t("Service")}</th>
								<th className="cp-col-qty">{t("Qty")}</th>
								<th className="cp-col-amount">{t("Price")}</th>
								<th className="cp-col-action" />
							</tr>
						</thead>
						<tbody>
							{serviceItems.map((item) => (
								<tr key={item.id}>
									<td>
										{isReadOnlyService(item) ? (
											<div className="cp-line-name">
												<span>{item.service_name}</span>
												{item.isConsultation ? (
													<SourceBadge variant="consultation">{t("Consultation")}</SourceBadge>
												) : (
													<SourceBadge variant="prescribed">{t("Prescribed")}</SourceBadge>
												)}
											</div>
										) : (
											<Form.Select
												size="sm"
												value={item.service_id || ""}
												onChange={(e) => selectServiceForRow(item.id, e.target.value)}
												{...disableScrollProps}
											>
												<option value="">{t("Select service")}</option>
												{generalServiceOptions.map((opt) => (
													<option
														key={opt.value}
														value={opt.value}
														disabled={
															usedServiceIds.has(String(opt.value)) &&
															String(item.service_id) !== String(opt.value)
														}
													>
														{opt.label}
													</option>
												))}
											</Form.Select>
										)}
									</td>
									<td>
										<Form.Control
											type="number"
											size="sm"
											min={1}
											className="cp-qty-input"
											value={item.quantity ?? 1}
											disabled={isSelectableService(item) && !item.service_id}
											onChange={(e) =>
												updateServiceQuantity(
													item.id,
													Math.max(1, parseInt(e.target.value, 10) || 1)
												)
											}
											{...disableScrollProps}
										/>
									</td>
									<td className="text-end">
										<InputGroup size="sm" className="cp-amount-group">
											<InputGroup.Text>{getCurrencySymbol(displayCurrency)}</InputGroup.Text>
											<LineAmountInput
												item={item}
												updateFn={updateServiceAmount}
												disabled={isSelectableService(item) && !item.service_id}
												{...amountInputProps}
											/>
										</InputGroup>
									</td>
									<td className="cp-col-action">
										{isSelectableService(item) && (
											<button
												type="button"
												className="cp-remove-btn"
												onClick={() => removeServiceItem(item.id)}
												aria-label={t("Remove service")}
											>
												<i className="fa fa-trash" />
											</button>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</div>
				<Button variant="outline-primary" size="sm" className="cp-add-btn" onClick={addServiceRow}>
					<i className="fa fa-plus me-1" />
					{t("Add Service")}
				</Button>
			</SectionBlock>

			{showLabSection && (
			<SectionBlock
				accent="investigations"
				icon="fa-flask"
				title={t("Lab Investigations")}
				total={billingSummary.investigation_total ?? 0}
				currency={displayCurrency}
				getCurrencySymbol={getCurrencySymbol}
				isOpen={sectionOpen.investigations}
				onToggle={() => toggleSection("investigations")}
				itemCount={investigationItems.length}
				footer={
					<>
						<SectionDivider title={t("Discount & Tax")} />
						<div className="cp-adjustments-body">
							<BillingInvestigationsAdjustments
								t={t}
								investigationsBilling={investigationsBillingTotals}
								investigationsTaxConfig={investigationsTaxConfig}
								investigationsDiscountType={investigationsDiscountType}
								investigationsDiscountValue={investigationsDiscountValue}
								onDiscountTypeChange={(type) =>
									updateInvestigationsDiscount({ discountType: type })
								}
								onDiscountValueChange={(value) =>
									updateInvestigationsDiscount({ discountValue: value })
								}
								onTaxConfigChange={updateInvestigationsTaxConfig}
								displayCurrency={displayCurrency}
								getCurrencySymbol={getCurrencySymbol}
								posTaxType={posTaxType}
								pharmacyStateCode={pharmacyStateCode}
								patientStateCode={patientStateCode}
								disableScrollProps={disableScrollProps}
								showTax={showLabTax}
							/>
						</div>
					</>
				}
			>
				<div className="table-responsive cp-table-wrap">
					<Table size="sm" className="cp-table mb-0">
						<thead>
							<tr>
								<th>{t("Test")}</th>
								<th className="cp-col-qty">{t("Qty")}</th>
								<th className="cp-col-amount">{t("Price")}</th>
								<th className="cp-col-action" />
							</tr>
						</thead>
						<tbody>
							{investigationItems.length > 0 ? (
								investigationItems.map((item) => (
									<tr key={item.id}>
										<td>
											{isReadOnlyInvestigation(item) ? (
												<div className="cp-line-name">
													<span>{item.test_name}</span>
													<SourceBadge variant="prescribed">{t("Prescribed")}</SourceBadge>
												</div>
											) : (
												<Form.Select
													size="sm"
													value={item.test_id || ""}
													onChange={(e) =>
														selectInvestigationForRow(item.id, e.target.value)
													}
													{...disableScrollProps}
												>
													<option value="">{t("Select test")}</option>
													{labInvestigationOptions.map((opt) => (
														<option
															key={opt.value}
															value={opt.value}
															disabled={
																usedTestIds.has(String(opt.value)) &&
																String(item.test_id) !== String(opt.value)
															}
														>
															{opt.label}
														</option>
													))}
												</Form.Select>
											)}
										</td>
										<td>
											<Form.Control
												type="number"
												size="sm"
												min={1}
												className="cp-qty-input"
												value={item.quantity ?? 1}
												disabled={!item.test_id}
												onChange={(e) =>
													updateInvestigationQuantity(
														item.id,
														Math.max(1, parseInt(e.target.value, 10) || 1)
													)
												}
												{...disableScrollProps}
											/>
										</td>
										<td className="text-end">
											<InputGroup size="sm" className="cp-amount-group">
												<InputGroup.Text>
													{getCurrencySymbol(displayCurrency)}
												</InputGroup.Text>
												<LineAmountInput
													item={item}
													updateFn={updateInvestigationAmount}
													disabled={!item.test_id}
													{...amountInputProps}
												/>
											</InputGroup>
										</td>
										<td className="cp-col-action">
											<button
												type="button"
												className="cp-remove-btn"
												onClick={() => removeInvestigation(item.id)}
												aria-label={t("Remove investigation")}
											>
												<i className="fa fa-trash" />
											</button>
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={4} className="text-center text-muted py-3">
										{t("No lab tests added")}
									</td>
								</tr>
							)}
						</tbody>
					</Table>
				</div>
				<Button
					variant="outline-primary"
					size="sm"
					className="cp-add-btn"
					onClick={addInvestigationRow}
				>
					<i className="fa fa-plus me-1" />
					{t("Add Investigation")}
				</Button>
			</SectionBlock>
			)}

			{showMedicinesSection && (
				<SectionBlock
					accent="medicines"
					icon="fa-medkit"
					title={t("Pharmacy")}
					total={hasPharmacyPurchases ? billingSummary.pharmacy_total ?? 0 : null}
					showTotal={hasPharmacyPurchases}
					currency={displayCurrency}
					getCurrencySymbol={getCurrencySymbol}
					isOpen={sectionOpen.medicines}
					onToggle={() => toggleSection("medicines")}
					itemCount={hasPharmacyPurchases ? pharmacyItems.length : prescriptions.length}
				>
					<PrescribedMedicinesBlock prescriptions={prescriptions} t={t} />
					{hasPharmacyPurchases && prescriptions.length > 0 && (
						<div className="cp-section-divider" />
					)}
					<PharmacyPurchasesBlock
						pharmacyItems={pharmacyItems}
						displayCurrency={displayCurrency}
						getCurrencySymbol={getCurrencySymbol}
						t={t}
					/>
				</SectionBlock>
			)}
		</div>
	);
};

export default CollectPaymentBillingPanel;
